import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, format } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';

import { cn } from '../../lib/cn';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';

export interface DateTimeDisplayProps extends React.HTMLAttributes<HTMLTimeElement> {
  /** ISO string or Date. Renders em-dash placeholder when null/undefined. */
  value: string | Date | null | undefined;
  /** Override locale; otherwise inferred from i18n. */
  locale?: 'cs' | 'en';
  /** Date-fns format string for the absolute display. */
  absoluteFormat?: string;
}

function pickLocale(code: string | undefined) {
  if (!code) return enUS;
  if (code.toLowerCase().startsWith('cs')) return cs;
  return enUS;
}

export const DateTimeDisplay = React.forwardRef<HTMLTimeElement, DateTimeDisplayProps>(
  ({ className, value, locale, absoluteFormat = 'dd.MM.yyyy HH:mm zzz', ...props }, ref) => {
    const { i18n } = useTranslation();
    const date =
      value === null || value === undefined
        ? null
        : typeof value === 'string'
          ? new Date(value)
          : value;
    const dfLocale =
      locale === 'cs' ? cs : locale === 'en' ? enUS : pickLocale(i18n?.language);
    const isInvalid = !date || Number.isNaN(date.getTime());
    const relative =
      isInvalid || !date
        ? '—'
        : formatDistanceToNow(date, { addSuffix: true, locale: dfLocale });
    const absolute =
      isInvalid || !date ? '' : format(date, absoluteFormat, { locale: dfLocale });
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <time
              ref={ref}
              dateTime={isInvalid || !date ? undefined : date.toISOString()}
              className={cn('text-sm text-[var(--color-fg-secondary)]', className)}
              {...props}
            >
              {relative}
            </time>
          </TooltipTrigger>
          {!isInvalid ? <TooltipContent>{absolute}</TooltipContent> : null}
        </Tooltip>
      </TooltipProvider>
    );
  },
);
DateTimeDisplay.displayName = 'DateTimeDisplay';
